<template>
  <div class="w-100">
    <div v-for="(bank, index) in banks" :key="index" class="bank-entry">
      <hr v-if="index > 0" class="mt-4" />

      <form-bank
        :label="'Nome e dados do seu banco nacional (no Brasil)'"
        v-model="bank.selectedBank"
        :id="`banks[${index}].id`"
        :name="`banks[${index}].id`"
        :options="bankOptions"
      />

      <div class="row g-2">
        <form-input
          class="col-6 form-input mb-3"
          label="Agência"
          v-model="bank.agency"
          :id="`banks[${index}].agency`"
          :name="`banks[${index}].agency`"
          type="tel"
        />

        <form-input
          class="col-6 form-input mb-3"
          label="Conta"
          v-model="bank.account"
          :id="`banks[${index}].account`"
          :name="`banks[${index}].account`"
          type="tel"
        />

        <form-input
          v-if="!allowMultiple"
          class="col-12 form-input mb-3"
          label="Titular da conta"
          v-model="bank.accountOwnerName"
          :id="`banks[${index}].ownerName`"
          :name="`banks[${index}].ownerName`"
        />
      </div>

      <form-button
        v-if="index > 0"
        class="col-12 form-input mt-0"
        title="Remover"
        buttonColor="secondary remove"
        @click="removeBank(index)"
      />
    </div>

    <div v-if="allowMultiple" class="col-12 d-flex justify-content-center mb-1">
      <img :src="lineImg" height="24" />
    </div>

    <form-button
      v-if="allowMultiple"
      class="col-12 form-input"
      buttonColor="add mt-0"
      title="Adicionar conta bancária"
      @clicked="addBank"
    />
  </div>
</template>

<script>
import { formButton, formInput } from 'tb-design_system-web'
import { ref, watch, inject, onMounted } from 'vue'

import formBank from '@/components/form/bank.vue'
import { getBanks } from '@/services/api'
import { handleGlobalError } from '@/services/errorHandler'
import { useCache } from '@/stores/cache'

export default {
  name: 'FormBankAccount',
  components: {
    formButton,
    formInput,
    formBank
  },
  props: {
    modelValue: {
      type: Array,
      required: true
    },
    allowMultiple: {
      type: Boolean,
      default: true
    }
  },
  setup(props, { emit }) {
    const alreadySubmittedOnce = inject('alreadySubmittedOnce', ref(false))
    const cacheStore = useCache()

    const banks = ref([...props.modelValue])
    const bankOptions = ref([])

    const lineImg = new URL('@/assets/img/svg/line.svg', import.meta.url).href

    const fetchBanks = async () => {
      try {
        const response = await getBanks()
        const formatted = response.data.payload.map((item) => ({
          key: item.id,
          name: `${item.id} - ${item.name}`
        }))
        cacheStore.setFinishCache(formatted)
        bankOptions.value = formatted
      } catch (err) {
        handleGlobalError(err)
      }
    }

    onMounted(async () => {
      const cachedBanks = cacheStore.getBankCache
      if (Array.isArray(cachedBanks) && cachedBanks.length > 0) {
        bankOptions.value = cachedBanks
      } else {
        await fetchBanks()
      }

      banks.value.forEach((bank) => {
        const match = bankOptions.value.find((opt) => opt.key === bank.code)
        if (match) {
          bank.selectedBank = match
        }
      })
    })

    watch(
      banks,
      (val) => {
        if (!val.length) return emit('update:modelValue', [])

        const formatted = val.map((item) => ({
          account: item.account,
          agency: item.agency,
          ...(props.allowMultiple ? {} : { accountOwnerName: item.accountOwnerName }),
          code: item.selectedBank?.key || '',
          name: item.selectedBank?.name || ''
        }))

        emit('update:modelValue', formatted)
      },
      { deep: true }
    )

    const addBank = () => {
      banks.value.push({
        code: '',
        agency: '',
        account: '',
        name: '',
        selectedBank: null,
        ...(props.allowMultiple ? {} : { accountOwnerName: '' })
      })
    }

    const removeBank = (index) => {
      banks.value.splice(index, 1)
    }

    const nameWithLang = ({ name }) => `${name}`

    return {
      banks,
      bankOptions,
      addBank,
      removeBank,

      alreadySubmittedOnce,
      lineImg,
      nameWithLang
    }
  }
}
</script>

<style lang="scss">
.add {
  background-color: #d7d7d7 !important;
  border-color: #d7d7d7 !important;
  color: #000 !important;
}

.remove {
  margin-top: 5px !important;
}

.multiselect {
  .multiselect__tags {
    display: flex;
    align-items: center;
    height: 52px;
    border-radius: 9px;
    letter-spacing: 0.5px;
    border: solid 2px #e1e1e1;
    background-color: transparent;
    color: #000;
    padding-top: 0px;
  }

  .multiselect__select {
    height: 49px;
  }

  .multiselect__single {
    margin-bottom: 0px;
    font-size: 14px;
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block; /* Garante que funcione corretamente */
    max-width: 100%; /* Impede quebra */
  }

  .multiselect__input {
    height: 52px;
    margin-bottom: 0px;
    font-size: 14px;
    font-weight: 400;
    background-color: transparent;
  }

  .multiselect__option--highlight {
    background-color: #000;
    color: #fff;
  }

  .multiselect__placeholder {
    margin-bottom: 0px;
    padding-left: 5px;
    font-size: 14px;
    font-weight: 400;
  }

  .multiselect__content-wrapper {
    font-size: 12px;
    font-weight: 400;
  }
}
</style>
